import { useState, useEffect, useRef } from "react";
import axios from "axios";
import toast from "react-hot-toast";


// import {
//   Image as ImageIcon, Download, Calendar,
//   User as UserIcon, ChevronLeft, ChevronRight, Folder
// } from "lucide-react";

import {
  Image as ImageIcon,
  Eye,
  Calendar,
  User as UserIcon,
  ChevronLeft,
  ChevronRight,
  Folder
} from "lucide-react";




import { getEmployeesApi, getPublicScreenshotsApi } from "../../app/api";
import { createPortal } from "react-dom";

const USERS_PER_FOLDER_PAGE = 5;
const SCREENSHOTS_PER_PAGE = 8;

export default function PublicScreenshots() {
  const [screenshots, setScreenshots] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [previewIndex, setPreviewIndex] = useState(null);

  // Ref for screenshots grid
  const gridRef = useRef(null);

  // Compute today and yesterday in yyyy-MM-dd format
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const formatDate = (d) => d.toISOString().split('T')[0];
  const todayStr = formatDate(today);
  const yesterdayStr = formatDate(yesterday);

  // Screenshots pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Folder (user) pagination
  const [folderPage, setFolderPage] = useState(1);

  // const fetchData = async () => {
  //   setLoading(true);
  //   try {
  //     const [scResponse, empResponse] = await Promise.all([
  //       // axios.get("http://localhost:5555/api/screenshots/public"),
  //       // axios.get("https://uptask.visiontrek.io/api/screenshots/public"),



  //       getEmployeesApi().catch(() => ({ success: false, data: [] }))
  //     ]);

  //     if (scResponse.data && scResponse.data.success) {
  //       setScreenshots(scResponse.data.data || []);
  //     } else {
  //       toast.error("Failed to load screenshots data");
  //     }

  //     if (empResponse && empResponse.success) {
  //       setEmployees(empResponse.data || []);
  //     } else if (Array.isArray(empResponse)) {
  //       setEmployees(empResponse);
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     toast.error("Error connecting to APIs");
  //   } finally {
  //     setLoading(false);
  //   }
  // };



const fetchData = async () => {
  setLoading(true);

  try {
    const [scResponse, empResponse] = await Promise.all([
      getPublicScreenshotsApi().catch(() => ({ success: false, data: [] })),
      getEmployeesApi().catch(() => ({
        success: false,
        data: [],
      })),
    ]);

    if (scResponse && scResponse.success) {
      setScreenshots(scResponse.data || []);
    }

    if (empResponse.success) {
      setEmployees(empResponse.data || []);
    }
  } catch (err) {
    console.error(err);
    toast.error("Error connecting to APIs");
  } finally {
    setLoading(false);
  }
};




  useEffect(() => { fetchData(); }, []);

  // Map userId to employee name
  const getUserName = (userId) => {
    const emp = employees.find(e => e.id === Number(userId));
    if (emp) return `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || `User #${userId}`;
    return `User #${userId}`;
  };

  // Helper to extract date (yyyy-MM-dd) from screenshot object
  const extractDateFromScreenshot = (sc) => {
    // Try to parse timestamp from imagePath if it contains a numeric epoch
    const match = sc.imagePath?.match(/_(\d+)\./);
    if (match) {
      const date = new Date(Number(match[1]));
      return date.toISOString().split('T')[0];
    }
    // Fallback to createdAt field, assuming ISO string
    if (sc.createdAt) {
      return new Date(sc.createdAt).toISOString().split('T')[0];
    }
    return '';
  };

  // Check if a date string is within allowed range (today or yesterday)
  const isDateWithinAllowedRange = (dateStr) => {
    return dateStr === todayStr || dateStr === yesterdayStr;
  };

  // Reset screenshots page when filter changes (user or date)
  useEffect(() => { setCurrentPage(1); }, [selectedUser, selectedDate]);

  // Scroll to screenshots grid when a date is selected
  useEffect(() => {
    if (selectedDate && gridRef.current) {
      // Use a short timeout to ensure the DOM has updated with filtered results
      const timer = setTimeout(() => {
        gridRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [selectedDate]);

  const uniqueUserIds = [...new Set(screenshots.map(s => s.userId))];

  let filteredScreenshots = selectedUser === "all"
    ? screenshots
    : screenshots.filter(s => s.userId === Number(selectedUser));

  // Apply date filter if a date is selected
  if (selectedDate) {
    filteredScreenshots = filteredScreenshots.filter(sc => {
      const scDate = extractDateFromScreenshot(sc);
      return scDate === selectedDate;
    });
  }

  // Screenshots pagination
  const totalItems = filteredScreenshots.length;
  const totalPages = Math.ceil(totalItems / SCREENSHOTS_PER_PAGE);
  const indexOfLastItem = currentPage * SCREENSHOTS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - SCREENSHOTS_PER_PAGE;
  const currentScreenshots = filteredScreenshots.slice(indexOfFirstItem, indexOfLastItem);

  // Keyboard navigation for image preview carousel
  useEffect(() => {
    if (previewIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') {
        setPreviewIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'ArrowRight') {
        setPreviewIndex((prev) => {
          const maxIndex = currentScreenshots ? currentScreenshots.length - 1 : 0;
          return Math.min(prev + 1, maxIndex);
        });
      } else if (e.key === 'Escape') {
        setPreviewIndex(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [previewIndex]);

  // Group by user for folder view
  const groupedByUser = screenshots.reduce((acc, curr) => {
    if (!acc[curr.userId]) acc[curr.userId] = [];
    acc[curr.userId].push(curr);
    return acc;
  }, {});

  const allFolderEntries = Object.entries(groupedByUser);
  const totalFolderPages = Math.ceil(allFolderEntries.length / USERS_PER_FOLDER_PAGE);
  const folderIndexStart = (folderPage - 1) * USERS_PER_FOLDER_PAGE;
  const currentFolderEntries = allFolderEntries.slice(folderIndexStart, folderIndexStart + USERS_PER_FOLDER_PAGE);

  // Dot pagination renderer (shared)
  const renderDotPagination = (current, total, onChange) => {
    const pages = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, current - 1);
      let end = Math.min(total - 1, current + 1);
      if (current <= 3) end = Math.min(4, total - 1);
      if (current >= total - 2) start = Math.max(total - 3, 2);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return (
      <div className="flex items-center gap-2">
        <button
          onClick={() => onChange(Math.max(current - 1, 1))}
          disabled={current === 1}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#21124f] border border-purple-500/20 hover:bg-[#2c1868] text-white rounded-lg text-sm transition disabled:opacity-30 disabled:pointer-events-none"
        >
          <ChevronLeft size={14} /> Prev
        </button>

        {pages.map((page, i) =>
          page === "..." ? (
            <span key={`d-${i}`} className="px-1 text-purple-300 font-bold select-none">...</span>
          ) : (
            <button
              key={`p-${page}`}
              onClick={() => onChange(page)}
              className={`w-8 h-8 rounded-lg text-sm font-bold transition ${
                current === page
                  ? "bg-purple-600 text-white shadow shadow-purple-600/30"
                  : "bg-[#21124f] hover:bg-[#2c1868] border border-purple-500/20 text-purple-200"
              }`}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => onChange(Math.min(current + 1, total))}
          disabled={current === total}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#21124f] border border-purple-500/20 hover:bg-[#2c1868] text-white rounded-lg text-sm transition disabled:opacity-30 disabled:pointer-events-none"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    );
  };

  return (
    <div className="text-white min-h-screen md:mt-20">

      {/* HEADER */}
      <div className="flex md:flex-row flex-col items-center justify-between gap-4 mb-8 bg-gradient-to-br from-[#140c2b] via-[#1f1147] to-[#0f0825] p-5 rounded-2xl border border-purple-500/30 shadow-2xl">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-600">
            Public Screenshots
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Browse and monitor captured user sessions in structured repositories
          </p>
        </div>
        {/* Filters row — wraps on mobile */}
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-purple-200 font-semibold whitespace-nowrap">Repository:</label>
          <select
//             className="flex-1
//  min-w-[40px] bg-[#2a1768] text-white border
//  border-purple-500/40 rounded-lg px-3 py-2 cursor-pointer 
// hover:bg-[#341d82] transition focus:ring-2 focus:ring-purple-500
//  outline-none text-sm"
className="bg-[#2a1768] text-white border border-purple-500/40 rounded-lg px-4 py-2 cursor-pointer hover:bg-[#341d82] transition focus:ring-2 focus:ring-purple-500 outline-none"
            value={selectedUser}
            onChange={e => {
              setSelectedUser(e.target.value);
              setTimeout(() => { if (gridRef.current) gridRef.current.scrollIntoView({ behavior: 'smooth' }); }, 100);
            }}
          >
            <option value="all">All Users</option>
            {uniqueUserIds.map(u => (
              <option key={u} value={u}>{getUserName(u)} (ID: {u})</option>
            ))}
          </select>

          <label className="text-sm text-purple-200 font-semibold whitespace-nowrap">Date:</label>
          <input
            type="date"
            // className="flex-1 min-w-[130px] bg-[#2a1768] text-white border border-purple-500/40 rounded-lg px-2 py-2 cursor-pointer hover:bg-[#341d82] transition focus:ring-2 focus:ring-purple-500 outline-none text-sm"
           
className="bg-[#2a1768] text-white border border-purple-500/40 rounded-lg px-2 py-1 cursor-pointer hover:bg-[#341d82] transition focus:ring-2 focus:ring-purple-500 outline-none"
 value={selectedDate}
            min={yesterdayStr}
            max={todayStr}
            onChange={e => setSelectedDate(e.target.value)}
          />
          {/* Reset Filters Button */}
          <button
            className="px-3 py-2 rounded-lg bg-purple-700 hover:bg-purple-600 text-white transition text-sm font-semibold whitespace-nowrap"
            onClick={() => {
              setSelectedUser('all');
              setSelectedDate('');
              setCurrentPage(1);
              setFolderPage(1);
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {loading ? (
        <div className=" bg-[#201446] rounded-lg flex flex-col items-center justify-center p-20 text-white/40">
          <div className="w-12 h-12 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin mb-4" />
          <p className="text-lg">Loading screenshots from system...</p>
        </div>
      ) : screenshots.length === 0 ? (
        <div className=" bg-[#1e1247] rounded-lg flex flex-col items-center justify-center p-20 text-white/40">
          <ImageIcon size={64} className="mb-4 opacity-50" />
          <p className="text-lg">No screenshots have been uploaded yet.</p>
        </div>
      ) : (
        <>
          {/* ── USER STORAGE REPOSITORIES ── */}
          <div className="mb-10 bg-[#150a33] p-6 rounded-2xl border border-purple-500/20 shadow-xl">

            {/* Folder header row */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
              <h2 className="text-xl font-bold text-purple-300 flex items-center gap-2">
                <Folder className="text-purple-400" size={22} /> User Storage Repositories
                <span className="text-xs text-purple-400/60 font-normal ml-1">
                  ({allFolderEntries.length} users)
                </span>
              </h2>
              {selectedUser !== "all" && (
                <button
                  onClick={() => { setSelectedUser("all"); setFolderPage(1); }}
                  className="px-4 py-1.5 rounded-lg border-2 border-dashed border-purple-500/40 bg-purple-950/20 hover:bg-purple-950/40 text-purple-300 hover:scale-[1.03] text-sm font-semibold transition-all duration-300"
                >
                  Show All Repositories
                </button>
              )}
            </div>

            {/* 5 folder cards per page */}
            <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-purple-600 scrollbar-track-purple-200">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-5">
               {currentFolderEntries.map(([userId, userScs]) => {
                 const isSelected = selectedUser === userId;
                 return (
                   <div
                     key={userId}
                     onClick={() => {
                       setSelectedUser(userId);
                       // Tap-to-scroll: scroll to screenshots grid after selection
                       setTimeout(() => {
                         if (gridRef.current) {
                           gridRef.current.scrollIntoView({ behavior: 'smooth' });
                         }
                       }, 100);
                     }}
                     className={`cursor-pointer p-4 rounded-xl border transition-all duration-300 flex flex-col items-center justify-center gap-2 hover:scale-[1.03] shadow-md ${
                       isSelected
                         ? "bg-[#381e7d] border-purple-400 ring-2 ring-purple-400/40"
                         : "bg-[#21124f] hover:bg-[#2b1766] border-purple-500/20"
                     }`}
                   >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
                      <UserIcon size={20} className="text-white" />
                    </div>
                    <span className="font-bold text-sm text-center text-white truncate max-w-full px-1">
                      {getUserName(userId)}
                    </span>
                    <span className="text-xs text-purple-300/80 font-medium">ID: {userId}</span>
                    <span className="text-xs px-2 py-0.5 bg-black/30 rounded-full text-purple-200 mt-1">
                      {userScs.length} files
                    </span>
                  </div>
                );
              })}
            </div>

            </div>{/* Folder pagination — only when > 5 users */}
            {totalFolderPages > 1 && (
              <div className="flex justify-center pt-2">
                {renderDotPagination(folderPage, totalFolderPages, setFolderPage)}
              </div>
            )}
          </div>

          {/* ── SCREENSHOTS GRID ── */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-3">
            <h2 className="text-xl font-bold text-purple-300 flex items-center gap-2">
              <span className="w-2 h-6 bg-purple-500 rounded-full" />
              {selectedUser === "all" ? "All Screenshots" : `Screenshots — ${getUserName(selectedUser)}`}
            </h2>
            <span className="text-xs text-white/50 bg-[#1c0f3c] border border-purple-500/10 px-3 py-1 rounded-full">
              Showing {Math.min(indexOfFirstItem + 1, totalItems)}–{Math.min(indexOfLastItem, totalItems)} of {totalItems} items
            </span>
          </div>

          <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {currentScreenshots.map((sc, idx) => (
              <div
                key={sc.id}
                onClick={() => setPreviewIndex(idx)}
                className="relative group rounded-xl overflow-hidden shadow-xl border border-purple-500/10 bg-[#1a0f35] hover:shadow-purple-500/20 transition-all duration-300"
              >
                <img
                  src={`https://uptask.visiontrek.io/${sc.imagePath}`}
                  alt={`Screenshot ${sc.id}`}
                  loading="lazy"
                  className="w-full h-[220px] object-cover object-top group-hover:scale-105 transition duration-500 cursor-pointer"
                  onError={e => {
                    e.target.src =
                      "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=500&auto=format&fit=crop&q=60";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0e0722] via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-white truncate max-w-[150px]">{getUserName(sc.userId)}</p>
                      <p className="text-xs text-purple-300 font-medium">User ID: {sc.userId}</p>
                      <p className="text-xs text-white/60 flex items-center gap-1 mt-1 font-mono">
                        <Calendar size={12} />
                        {(() => {
                          const match = sc.imagePath.match(/_(\d+)\./);
                          const date = match ? new Date(Number(match[1])) : new Date(sc.createdAt.replace(" ", "T") + "Z");
                          return date.toLocaleString("en-IN", {
                            timeZone: "Asia/Kolkata",
                          });
                        })()}
                      </p>
                    </div>
                    <a
                      href={`https://uptask.visiontrek.io/${sc.imagePath}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-purple-600/90 p-2.5 rounded-full hover:bg-purple-500 transition shadow-lg"
                      title="View Screenshot"
                    >
                      <Eye size={14} className="text-white" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Screenshots pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-12 pb-10">
              {renderDotPagination(currentPage, totalPages, setCurrentPage)}
            </div>
          )}
        </>
      )}

      {previewIndex !== null && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          onClick={() => setPreviewIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setPreviewIndex(null)}
            className="absolute top-4 right-4 z-10 text-white text-3xl font-bold bg-black/50 w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/80 transition"
          >
            ×
          </button>

          {/* Prev button */}
          <button
            onClick={(e) => { e.stopPropagation(); setPreviewIndex(prev => Math.max(prev - 1, 0)); }}
            disabled={previewIndex === 0}
            className="absolute left-2 md:left-6 z-10 text-white p-2 md:p-3 bg-purple-600/80 rounded-full hover:bg-purple-500 transition disabled:opacity-30 disabled:pointer-events-none"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Image — fills screen on mobile */}
          <div
            className="w-full h-full flex items-center justify-center px-12"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`https://uptask.visiontrek.io/${currentScreenshots[previewIndex].imagePath}`}
              alt="Preview"
              className="max-w-full max-h-screen w-auto h-auto object-contain rounded-none md:rounded-xl shadow-2xl"
            />
          </div>

          {/* Next button */}
          <button
            onClick={(e) => { e.stopPropagation(); setPreviewIndex(prev => Math.min(prev + 1, currentScreenshots.length - 1)); }}
            disabled={previewIndex === currentScreenshots.length - 1}
            className="absolute right-2 md:right-6 z-10 text-white p-2 md:p-3 bg-purple-600/80 rounded-full hover:bg-purple-500 transition disabled:opacity-30 disabled:pointer-events-none"
          >
            <ChevronRight size={24} />
          </button>

          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm bg-black/50 px-3 py-1 rounded-full">
            {previewIndex + 1} / {currentScreenshots.length}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
